/**
 * <s-spec-glance> — buy-box "at a glance" panel (snippets/product-specs.liquid, mode 'summary').
 *
 *   [data-tilt-zone]  each cell (Material, Size, Weight). The pointer position inside it tilts its [data-tilt]
 *                     so the side under the pointer presses down (gift-card feel), and sets --px / --py so
 *                     the metal sheen and sphere highlights follow the light. Touch: active while the finger is down
 *                     ([data-pressed] on the cell also fans the material spheres out).
 *   [data-dims]       cube faces / material plates; hovering or tapping one lights every face with the same
 *                     data-dims and the matching [data-dim] numbers or material names.
 * Reduced motion: no tilt or light movement, highlights still work. Re-rendered with the 'product-form' partial.
 */
// Phone "View in 3D" state survives the partial re-render on variant change.
// Phones open the 3D views by default, on Size (merchant 2026-11-01); desktop never uses this state.
let glanceExpanded = window.matchMedia('(max-width: 47.99rem)').matches;
let glanceSlide = null;

class SSpecGlance extends HTMLElement {
  #ac = null;

  connectedCallback() {
    this.#ac = new AbortController();
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.querySelectorAll('[data-tilt-zone]').forEach((zone) => this.#bind(zone, reduced));
    this.#bindCarousel();
  }

  /**
   * Phone carousel: the tab strip ([data-glance-tab]) mirrors the slide nearest the track's centre
   * ([data-glance-track] > cells). "View in 3D" opens it on Size (data-glance-start); tapping a tab opens it
   * if needed and scrolls to that slide. Expanded state and the current slide survive variant re-renders.
   */
  #bindCarousel() {
    const { signal } = this.#ac;
    const toggle = this.querySelector('[data-glance-toggle]');
    const track = this.querySelector('[data-glance-track]');
    const tabs = [...this.querySelectorAll('[data-glance-tab]')];
    const slides = track ? [...track.querySelectorAll(':scope > [data-tilt-zone]')] : [];
    if (glanceSlide === null) glanceSlide = Number(track?.dataset.glanceStart ?? 0);

    // A slide's left edge in the track's scroll coordinates (independent of offsetParent).
    const slideX = (slide) => track.scrollLeft + slide.getBoundingClientRect().left - track.getBoundingClientRect().left;
    const mark = (index) => {
      glanceSlide = index;
      tabs.forEach((tab, i) => tab.setAttribute('aria-current', String(i === index)));
      slides.forEach((slide, i) => slide.toggleAttribute('data-current', i === index));
    };
    const goTo = (index, smooth) => {
      const slide = slides[index];
      if (!slide || !track) return;
      const left = slideX(slide) - (track.clientWidth - slide.offsetWidth) / 2;
      const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      track.scrollTo({ left, behavior: smooth && !reduced ? 'smooth' : 'instant' });
      mark(index);
    };
    const apply = () => {
      this.toggleAttribute('data-expanded', glanceExpanded);
      toggle?.setAttribute("aria-expanded", String(glanceExpanded));
      tabs.forEach((tab) => tab.setAttribute("aria-expanded", String(glanceExpanded)));
      // The track is display:none until expanded, so position it on the next frame.
      if (glanceExpanded) requestAnimationFrame(() => goTo(glanceSlide, false));
    };

    // Re-open on this panel's own Size slide (a panel that was hidden could not measure its track).
    this.showStart = () => {
      const start = Number(track?.dataset.glanceStart ?? 0);
      mark(start);
      if (glanceExpanded) requestAnimationFrame(() => goTo(start, false));
    };
    mark(glanceSlide);
    apply();

    toggle?.addEventListener(
      'click',
      () => {
        glanceExpanded = !glanceExpanded;
        if (glanceExpanded) glanceSlide = Number(track?.dataset.glanceStart ?? 0);
        mark(glanceSlide);
        apply();
      },
      { signal },
    );

    tabs.forEach((tab, index) =>
      tab.addEventListener(
        'click',
        () => {
          if (!glanceExpanded) {
            glanceExpanded = true;
            glanceSlide = index;
            mark(index);
            apply();
          } else if (index === glanceSlide) {
            // Tapping the lit tab again folds the 3D views away (there is no separate text toggle).
            glanceExpanded = false;
            apply();
          } else {
            goTo(index, true);
          }
        },
        { signal },
      ),
    );

    let frame = 0;
    track?.addEventListener(
      'scroll',
      () => {
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(() => {
          const centre = track.scrollLeft + track.clientWidth / 2;
          let best = 0;
          let bestDistance = Infinity;
          slides.forEach((slide, i) => {
            const distance = Math.abs(slideX(slide) + slide.offsetWidth / 2 - centre);
            if (distance < bestDistance) {
              bestDistance = distance;
              best = i;
            }
          });
          if (best !== glanceSlide) mark(best);
        });
      },
      { passive: true, signal },
    );
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  #bind(zone, reduced) {
    const { signal } = this.#ac;
    const tilt = zone.querySelector('[data-tilt]');
    let pressed = false;

    const move = (event) => {
      if (reduced || !tilt) return;
      if (event.pointerType === 'touch' && !pressed) return;
      const rect = zone.getBoundingClientRect();
      const fx = Math.max(0, Math.min(1, (event.clientX - rect.left) / rect.width));
      const fy = Math.max(0, Math.min(1, (event.clientY - rect.top) / rect.height));
      const nx = fx * 2 - 1;
      const ny = fy * 2 - 1;
      tilt.dataset.tracking = '';
      // Positive rotateY sends the right edge away, positive rotateX the top edge: the side under the pointer sinks.
      tilt.style.setProperty('--ry', `${(nx * 16).toFixed(2)}deg`);
      tilt.style.setProperty('--rx', `${(-ny * 12).toFixed(2)}deg`);
      zone.style.setProperty('--px', `${Math.round(fx * 100)}%`);
      zone.style.setProperty('--py', `${Math.round(fy * 100)}%`);
    };

    const release = (keepHighlight = false) => {
      pressed = false;
      delete zone.dataset.pressed;
      if (tilt) {
        delete tilt.dataset.tracking;
        tilt.style.setProperty('--rx', '0deg');
        tilt.style.setProperty('--ry', '0deg');
      }
      zone.style.removeProperty('--px');
      zone.style.removeProperty('--py');
      if (!keepHighlight) this.#highlight(zone, null);
    };

    zone.addEventListener('pointerdown', (event) => {
      pressed = true;
      zone.dataset.pressed = '';
      move(event);
      this.#highlight(zone, event.target.closest('[data-dims]'));
    }, { signal });
    zone.addEventListener('pointermove', move, { signal });
    zone.addEventListener('pointerleave', (event) => { if (event.pointerType !== 'touch') release(); }, { signal });
    // A tap keeps the tapped face (and its numbers) lit until the next tap, so it can be read on a phone.
    zone.addEventListener('pointerup', (event) => { if (event.pointerType === 'touch') release(true); }, { signal });
    zone.addEventListener('pointercancel', () => release(), { signal });
    zone.addEventListener('pointerover', (event) => {
      if (event.pointerType === 'touch') return;
      this.#highlight(zone, event.target.closest('[data-dims]'));
    }, { signal });
  }

  #highlight(zone, face) {
    if (!zone.querySelector('[data-dims]')) return;
    const dims = face ? face.dataset.dims.split(' ') : [];
    zone.querySelectorAll('[data-dims]').forEach((node) => node.toggleAttribute('data-active', Boolean(face) && node.dataset.dims === face.dataset.dims));
    zone.querySelectorAll('[data-dim]').forEach((node) => node.toggleAttribute('data-active', dims.includes(node.dataset.dim)));
    // Phone: the Size tab above the carousel shows the same numbers, so light them there too.
    this.querySelectorAll('[data-glance-tab] [data-dim]').forEach((node) => node.toggleAttribute('data-active', dims.includes(node.dataset.dim)));
  }
}

if (!customElements.get('s-spec-glance')) customElements.define('s-spec-glance', SSpecGlance);
